(() => {
  if (window.efaSpecificProductsInit) { window.efaSpecificProductsInit(document); return; }
  const cleanups = new WeakMap();
  const gridCleanups = new WeakMap();
  const initGrid = grid => {
    if (gridCleanups.has(grid)) return;
    let frame;
    const place = () => {
      frame = null;
      const rows = [...grid.children].filter(card => card.matches('[data-specific-grid-row]'));
      const cards = [...grid.children].filter(card => !card.matches('[data-specific-grid-row]'));
      if (!rows.length || !cards.length) return;
      // Measure the actual rendered rows, including colour siblings and editorial cards.
      // Exclude inserted blocks during measurement so an old mobile position cannot
      // split a desktop product row when the viewport or grid density changes.
      const displays = rows.map(row => ({value:row.style.getPropertyValue('display'), priority:row.style.getPropertyPriority('display')}));
      rows.forEach(row => row.style.setProperty('display', 'none', 'important'));
      const lines = [];
      cards.forEach(card => {
        const top = Math.round(card.getBoundingClientRect().top);
        if (!lines.length || Math.abs(lines[lines.length - 1].top - top) > 2) lines.push({top, card});
      });
      rows.forEach((row, index) => {
        if (displays[index].value) row.style.setProperty('display', displays[index].value, displays[index].priority);
        else row.style.removeProperty('display');
      });
      rows.sort((a, b) => Number(a.dataset.specificGridRow) - Number(b.dataset.specificGridRow));
      const groups = new Map();
      let productLine = 0;
      let renderedLine = 1;
      rows.forEach(row => {
        const line = Math.max(1, Number(row.dataset.specificGridRow) || 1);
        productLine += Math.max(0, line - renderedLine);
        const before = lines[productLine]?.card || null;
        renderedLine = Math.max(line, renderedLine) + 1;
        if (!groups.has(before)) groups.set(before, []);
        groups.get(before).push(row);
      });
      groups.forEach((group, before) => {
        let anchor = before;
        group.slice().reverse().forEach(row => {
          if (row.nextElementSibling !== anchor) grid.insertBefore(row, anchor);
          anchor = row;
        });
      });
    };
    const request = () => { if (!frame) frame = requestAnimationFrame(place); };
    const resize = new ResizeObserver(request);
    resize.observe(grid);
    const mutation = new MutationObserver(request);
    mutation.observe(grid, {childList:true});
    gridCleanups.set(grid, () => {resize.disconnect(); mutation.disconnect(); cancelAnimationFrame(frame); gridCleanups.delete(grid);});
    place();
  };
  const init = (scope) => {
    scope.querySelectorAll('#collection').forEach(initGrid);
    const roots = [...scope.querySelectorAll('[data-efa-specific]')];
    if (scope.matches?.('[data-efa-specific]')) roots.unshift(scope);
    roots.forEach(root => {
      if (cleanups.has(root)) return;
      const list = root.querySelector('[data-specific-list]');
      const controls = root.querySelector('.efa-specific__controls');
      const prev = root.querySelector('[data-specific-prev]');
      const next = root.querySelector('[data-specific-next]');
      const pause = root.querySelector('[data-specific-pause]');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = matchMedia('(max-width: 760px)');
      const abort = new AbortController();
      const options = {signal: abort.signal};
      let timer, visible = false, hovering = false, focusing = false, chosenPause = false, editorSelected = false, deferUntil = 0;
      let animation;
      const cancelAnimation = () => {cancelAnimationFrame(animation); animation = null; list.style.removeProperty('scroll-snap-type');};
      const animateTo = position => {
        cancelAnimation();
        const start = list.scrollLeft;
        const started = performance.now();
        list.style.scrollSnapType = 'none';
        const step = now => {
          const progress = Math.min(1, (now - started) / 300);
          const eased = 1 - Math.pow(1 - progress, 3);
          list.scrollTo({left:start + (position - start) * eased, behavior:'instant'});
          if (progress < 1) animation = requestAnimationFrame(step);
          else cancelAnimation();
        };
        animation = requestAnimationFrame(step);
      };
      const isScroll = () => (mobile.matches ? root.dataset.mobileMode : root.dataset.mode) === 'scroll';
      const canMove = () => isScroll() && list.scrollWidth > list.clientWidth + 2;
      const max = () => Math.max(0, list.scrollWidth - list.clientWidth);
      const go = (direction, auto = false) => {
        const card = list.firstElementChild;
        if (!card || !canMove()) return;
        const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
        const distance = card.getBoundingClientRect().width + gap;
        let position = list.scrollLeft + direction * distance;
        if (auto && list.scrollLeft >= max() - 2) position = 0;
        position = Math.min(max(), Math.max(0, position));
        if (auto && !motion.matches) animateTo(position);
        else {cancelAnimation(); list.scrollTo({left:position, behavior:motion.matches ? 'instant' : 'smooth'});}
      };
      const measure = () => root.style.setProperty('--specific-card-height', `${Math.max(0, ...Array.from(list.children, card => card.getBoundingClientRect().height))}px`);
      const refresh = () => {
        controls.hidden = !canMove();
        pause.hidden = root.dataset.auto !== 'true' || motion.matches;
        prev.disabled = list.scrollLeft <= 2;
        next.disabled = list.scrollLeft >= max() - 2;
      };
      const stop = () => {clearTimeout(timer); timer = null;};
      const schedule = () => {
        if (root.dataset.auto !== 'true' || mobile.matches || motion.matches || !canMove() || !visible || hovering || focusing || chosenPause || editorSelected || document.hidden) {stop(); return;}
        if (timer) return;
        timer = setTimeout(() => {
          timer = null;
          if (Date.now() >= deferUntil) go(1, true);
          schedule();
        }, Math.max(1000, Number(root.dataset.interval) * 1000 || 4000, deferUntil - Date.now()));
      };
      prev.addEventListener('click', () => {deferUntil = Date.now() + 2000; go(-1);}, options);
      next.addEventListener('click', () => {deferUntil = Date.now() + 2000; go(1);}, options);
      pause.addEventListener('click', () => {
        chosenPause = !chosenPause;
        pause.setAttribute('aria-pressed', String(chosenPause));
        pause.setAttribute('aria-label', chosenPause ? pause.dataset.playLabel : pause.dataset.pauseLabel);
        pause.querySelector('[data-specific-symbol]').textContent = chosenPause ? '▶' : 'Ⅱ';
        schedule();
      }, options);
      root.addEventListener('pointerenter', () => {hovering = true; schedule();}, options);
      root.addEventListener('shopify:block:select', () => {editorSelected = true; schedule();}, options);
      root.addEventListener('shopify:block:deselect', () => {editorSelected = false; schedule();}, options);
      root.addEventListener('pointerleave', () => {hovering = false; schedule();}, options);
      root.addEventListener('focusin', () => {focusing = true; schedule();}, options);
      root.addEventListener('focusout', () => {queueMicrotask(() => {focusing = root.contains(document.activeElement); schedule();});}, options);
      list.addEventListener('pointerdown', () => {cancelAnimation(); deferUntil = Date.now() + 5000;}, options);
      list.addEventListener('scroll', refresh, {...options, passive: true});
      document.addEventListener('visibilitychange', schedule, options);
      const resize = new ResizeObserver(() => {measure(); refresh(); schedule();});
      resize.observe(list);
      resize.observe(root);
      const intersection = new IntersectionObserver(entries => {visible = entries[0].isIntersecting; schedule();});
      intersection.observe(root);
      const changed = () => {measure(); refresh(); schedule();};
      motion.addEventListener('change', changed);
      mobile.addEventListener('change', changed);
      cleanups.set(root, () => {
        stop(); cancelAnimation(); abort.abort(); resize.disconnect(); intersection.disconnect();
        motion.removeEventListener('change', changed); mobile.removeEventListener('change', changed); cleanups.delete(root);
      });
      // EFA 06/10/2026 : survol des pastilles couleur limite a SA carte.
      // Cause : custom-async.js (listScrollableAsync) parcourt `#collection.l4cl` avec querySelectorAll('li'), qui inclut
      // li.efa-specific-grid-row ; il attache alors a chaque pastille du bloc un mouseover qui ecrit data-img dans TOUTES les img
      // du bloc (autres cartes + vignette editoriale, jamais restauree car hors <picture>). On arrete l'evenement a la racine du bloc
      // (phase de capture, avant les ecouteurs poses sur la pastille) et on applique le meme echange d'image a la seule carte survolee.
      const memo = img => {
        if (!img.hasAttribute('data-src-initial') && img.hasAttribute('src')) img.setAttribute('data-src-initial', img.getAttribute('src'));
        if (!img.hasAttribute('data-srcset-initial') && img.hasAttribute('srcset')) img.setAttribute('data-srcset-initial', img.getAttribute('srcset'));
      };
      list.querySelectorAll('picture img').forEach(memo);
      root.addEventListener('mouseover', event => {
        const swatch = event.target.closest?.('.check.color li[data-img]');
        if (!swatch || !root.contains(swatch)) return;
        event.stopPropagation();
        const card = swatch.closest('[data-specific-list] > li');
        if (!card) return;
        const source = swatch.getAttribute('data-img');
        card.querySelectorAll('picture img').forEach(memo);
        card.querySelectorAll('img:not(.color-variant-img)').forEach(img => {
          if (img.closest('.check.color')) return;
          img.setAttribute('src', source);
          if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
        });
      }, {...options, capture: true});
      measure(); refresh(); schedule();
    });
  };
  window.efaSpecificProductsInit = init;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once:true});
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-efa-specific]').forEach(root => cleanups.get(root)?.()));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('#collection').forEach(grid => gridCleanups.get(grid)?.()));
  // Filtering and pagination replace the collection HTML without a section-load event.
  const changed = new MutationObserver(records => {
    records.forEach(record => {
      record.removedNodes.forEach(node => {
        if (node.nodeType !== 1 || node.isConnected) return;
        if (node.matches('[data-efa-specific]')) cleanups.get(node)?.();
        node.querySelectorAll('[data-efa-specific]').forEach(root => cleanups.get(root)?.());
        if (node.id === 'collection') gridCleanups.get(node)?.();
        node.querySelectorAll('#collection').forEach(grid => gridCleanups.get(grid)?.());
      });
      record.addedNodes.forEach(node => {
      if (node.nodeType !== 1) return;
      if (node.id === 'collection') initGrid(node);
      if (node.matches('[data-efa-specific]') || node.querySelector('[data-efa-specific]')) init(node);
      });
    });
  });
  changed.observe(document.documentElement, {childList:true, subtree:true});
})();

